import React from "react";
import PhaseCard from "./PhaseCard";
import ConnectionStatusCard from "./ConnectionStatusCard";
import MappingCard from "./MappingCard";

/**
 * PhaseTimeline — Vertical stack of progressive phase cards shown in chat.
 * The current actionable card can auto-expand to show inline status, input forms,
 * mapping UI, or the final integration summary.
 *
 * Props:
 *   cards: Array of { step_number, category, title, icon, tags, status }
 *   structuredData: last structured_data from the session (has connection / mapping info)
 *   lastCardContent: ReactNode — shown inside the final build card
 *   inputCardContent: ReactNode — inline input UI shown in the credentials card
 *   onConnectionProceed: optional callback for connection CTA
 *   onMappingProceed: optional callback for mapping CTA
 */
const PhaseTimeline = ({
  cards,
  structuredData,
  lastCardContent,
  inputCardContent,
  onConnectionProceed,
  onMappingProceed,
}) => {
  if (!cards || cards.length === 0) return null;

  const getCardContent = (card, isLast) => {
    const cat = card.category?.toUpperCase() || "";
    const sections = [];

    if (isLast && lastCardContent) {
      return <div className="space-y-3">{lastCardContent}</div>;
    }

    if ((cat.includes("CREDENTIAL") || cat.includes("CONNECT")) && structuredData?.type === "connection_status") {
      sections.push(
        <ConnectionStatusCard
          key="connection-status"
          data={structuredData.data}
          onProceed={onConnectionProceed || null}
        />
      );
    }

    if ((cat.includes("CREDENTIAL") || cat.includes("CONNECT")) && inputCardContent) {
      sections.push(<div key="input-form">{inputCardContent}</div>);
    }

    if (cat.includes("MAPPING") && structuredData?.type === "field_mapping") {
      sections.push(
        <MappingCard
          key="field-mapping"
          data={structuredData.data}
          onProceed={onMappingProceed || null}
        />
      );
    }

    if (sections.length > 0) {
      return <div className="space-y-3">{sections}</div>;
    }

    // Only show tag pills if the card has no details — details already surface
    // richer structured data so the raw tag list would be redundant.
    if (card.tags?.length > 0 && !card.details) {
      return (
        <div className="flex flex-wrap gap-1.5">
          {card.tags.map((tag, i) => (
            <span key={i} className="text-[0.7rem] font-semibold px-2.5 py-1 rounded-full border bg-blue-50 text-blue-600 border-blue-200 cursor-default select-none">{tag}</span>
          ))}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="w-full max-w-2xl my-3">
      <div className="flex flex-col gap-1.5">
        {cards.map((card, idx) => {
          const isLast = idx === cards.length - 1;
          const expandContent = getCardContent(card, isLast);
          const shouldAutoExpand = Boolean(
            expandContent && (card.status === "current" || (isLast && lastCardContent))
          );

          return (
            <PhaseCard
              key={idx}
              stepNumber={card.step_number}
              showStepNumber={true}
              category={card.category}
              title={card.title}
              icon={card.icon}
              tags={[]}
              status={card.status || "pending"}
              expandableContent={expandContent || undefined}
              defaultExpanded={shouldAutoExpand}
              details={card.details || null}
            />
          );
        })}
      </div>
    </div>
  );
};

export default PhaseTimeline;

